export const ERROR_CODE = {
  internal: 'internal_error',
  validation: 'validation_error',
  notFound: 'not_found',
  conflict: 'conflict',
  accountWrongState: 'account_in_wrong_state',
  unauthenticated: 'unauthenticated',
  forbidden: 'forbidden',
  rateLimited: 'rate_limited',
  dependencyUnavailable: 'dependency_unavailable',
  transport: 'transport',
  unknown: 'unknown',
} as const;
export type ErrorCode = (typeof ERROR_CODE)[keyof typeof ERROR_CODE];
export interface ErrorEnvelope {
  error: { code: string; message: string };
  request_id: string | null;
}
const KNOWN_CODES: ReadonlySet<string> = new Set(Object.values(ERROR_CODE));
export class HttpError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly serverMessage: string;
  readonly requestId: string | null;
  constructor(init: {
    code: ErrorCode;
    status: number;
    serverMessage?: string;
    requestId?: string | null;
  }) {
    super(init.serverMessage ?? init.code);
    this.name = 'HttpError';
    this.code = init.code;
    this.status = init.status;
    this.serverMessage = init.serverMessage ?? init.code;
    this.requestId = init.requestId ?? null;
  }
}
function codeFromStatus(status: number): ErrorCode {
  if (status === 401) return ERROR_CODE.unauthenticated;
  if (status === 403) return ERROR_CODE.forbidden;
  if (status === 404) return ERROR_CODE.notFound;
  if (status === 409) return ERROR_CODE.conflict;
  if (status === 422) return ERROR_CODE.validation;
  if (status === 429) return ERROR_CODE.rateLimited;
  if (status === 503) return ERROR_CODE.dependencyUnavailable;
  if (status >= 500) return ERROR_CODE.internal;
  return ERROR_CODE.unknown;
}
function isEnvelope(value: unknown): value is ErrorEnvelope {
  if (typeof value !== 'object' || value === null) return false;
  const e = (value as { error?: unknown }).error;
  return (
    typeof e === 'object' &&
    e !== null &&
    typeof (e as { code?: unknown }).code === 'string' &&
    typeof (e as { message?: unknown }).message === 'string'
  );
}
/**
 * pipe 的错误体与成功体同形：`{code, message, data, request_id}`，`code` 在顶层
 * （backend 是嵌在 `error` 里）。两棵树的 `code` 词表不一致，未知名回落到
 * HTTP 状态推导。
 */
function isFlatEnvelope(value: unknown): value is { code: string; message: string } & {
  request_id?: string | null;
} {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { code?: unknown }).code === 'string' &&
    typeof (value as { message?: unknown }).message === 'string'
  );
}
export function errorFromResponse(status: number, body: unknown): HttpError {
  if (isEnvelope(body)) {
    const raw = body.error.code;
    const code = KNOWN_CODES.has(raw) ? (raw as ErrorCode) : codeFromStatus(status);
    return new HttpError({
      code,
      status,
      serverMessage: body.error.message,
      requestId: body.request_id ?? null,
    });
  }
  if (isFlatEnvelope(body)) {
    const code = KNOWN_CODES.has(body.code) ? (body.code as ErrorCode) : codeFromStatus(status);
    return new HttpError({
      code,
      status,
      serverMessage: body.message,
      requestId: body.request_id ?? null,
    });
  }
  return new HttpError({ code: codeFromStatus(status), status });
}
export function toHttpError(error: unknown): HttpError {
  if (error instanceof HttpError) return error;
  if (error instanceof Error && error.name === 'AbortError') {
    return new HttpError({ code: ERROR_CODE.transport, status: 0, serverMessage: 'aborted' });
  }
  return new HttpError({
    code: ERROR_CODE.transport,
    status: 0,
    serverMessage: error instanceof Error ? error.message : String(error),
  });
}
export const ERROR_MESSAGE_KEY: Record<ErrorCode, string> = {
  [ERROR_CODE.internal]: 'errors.internal',
  [ERROR_CODE.validation]: 'errors.validation',
  [ERROR_CODE.notFound]: 'errors.notFound',
  [ERROR_CODE.conflict]: 'errors.conflict',
  [ERROR_CODE.accountWrongState]: 'errors.accountWrongState',
  [ERROR_CODE.unauthenticated]: 'errors.unauthenticated',
  [ERROR_CODE.forbidden]: 'errors.forbidden',
  [ERROR_CODE.rateLimited]: 'errors.rateLimited',
  [ERROR_CODE.dependencyUnavailable]: 'errors.dependencyUnavailable',
  [ERROR_CODE.transport]: 'errors.transport',
  [ERROR_CODE.unknown]: 'errors.unknown',
};
